import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ColumnOrderState, VisibilityState } from '@tanstack/react-table'
import { Columns3, Eye, EyeOff, GripVertical } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { REORDERABLE_COLUMNS } from '../constants'

export type ReorderableColumn = {
  id: string
  label: string
  clientOnly?: boolean
}

// Sortable column item component
interface SortableColumnItemProps {
  column: { id: string; label: string }
  isVisible: boolean
  onToggle: (columnId: string) => void
}

function SortableColumnItem({
  column,
  isVisible,
  onToggle,
}: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
        isDragging ? 'bg-primary/10' : ''
      }`}
    >
      <button
        type="button"
        className="cursor-grab touch-none active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical size={12} className="text-muted-foreground/50 shrink-0" />
      </button>
      <button
        type="button"
        onClick={() => onToggle(column.id)}
        className="flex flex-1 items-center gap-2"
      >
        {isVisible ? (
          <Eye size={14} className="text-green-500" />
        ) : (
          <EyeOff size={14} className="text-muted-foreground" />
        )}
        <span className={isVisible ? '' : 'opacity-50'}>{column.label}</span>
      </button>
    </div>
  )
}

interface ColumnVisibilityDropdownProps {
  columnVisibility: VisibilityState
  columnOrder: ColumnOrderState
  onToggle: (columnId: string) => void
  onShowAll: () => void
  onReorder: (newOrder: ColumnOrderState) => void
  isClientContext?: boolean
  reorderableColumns?: readonly ReorderableColumn[]
}

export function ColumnVisibilityDropdown({
  columnVisibility,
  columnOrder,
  onToggle,
  onShowAll,
  onReorder,
  isClientContext = false,
  reorderableColumns,
}: ColumnVisibilityDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Configure dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter and sort columns by their order in columnOrder
  const sortedColumns = useMemo(() => {
    const cols = reorderableColumns ?? REORDERABLE_COLUMNS
    // Filter out client-only columns when not in client context
    const filteredColumns = cols.filter(
      (col) => !('clientOnly' in col && col.clientOnly) || isClientContext,
    )
    return [...filteredColumns].sort((a, b) => {
      const aIdx = columnOrder.indexOf(a.id)
      const bIdx = columnOrder.indexOf(b.id)
      // If not in columnOrder, keep original order
      if (aIdx === -1 && bIdx === -1) return 0
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
  }, [columnOrder, isClientContext, reorderableColumns])

  const hiddenCount = sortedColumns.filter(
    (col) => columnVisibility[col.id] === false,
  ).length

  // Handle drag end - reorder columns
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = columnOrder.indexOf(active.id as string)
    const newIndex = columnOrder.indexOf(over.id as string)

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex)
      onReorder(newOrder)
    }
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
          hiddenCount > 0
            ? 'border-primary/60 bg-primary/10 text-primary dark:border-primary/40 dark:bg-primary/20 dark:text-primary/90'
            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <Columns3 size={14} />
        Columns
        {hiddenCount > 0 && (
          <span className="bg-primary text-primary-foreground rounded px-1.5 py-0.5 text-xs">
            {hiddenCount} hidden
          </span>
        )}
      </button>

      {isOpen && (
        <div className="bg-popover border-border absolute top-full right-0 z-30 mt-1 max-h-[400px] min-w-[240px] overflow-auto rounded-lg border py-2 shadow-xl">
          <div className="text-muted-foreground flex items-center justify-between px-3 py-1 text-xs tracking-wider uppercase">
            <span>Columns</span>
            <span className="text-muted-foreground/60 normal-case">
              Drag to reorder
            </span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedColumns.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {sortedColumns.map((column) => (
                <SortableColumnItem
                  key={column.id}
                  column={column}
                  isVisible={columnVisibility[column.id] !== false}
                  onToggle={onToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
          {hiddenCount > 0 && (
            <>
              <div className="border-border my-1 border-t" />
              <button
                type="button"
                onClick={() => {
                  onShowAll()
                  setIsOpen(false)
                }}
                className="text-primary hover:bg-muted flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors"
              >
                <Eye size={14} />
                Show all columns
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
